import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';

import { controlBase, controlStates } from '../../lib/control';
import { cn } from '../../lib/utils';
import { useAnchoredPanel } from '../anchored-panel';
import { Button } from '../shadcn/button';
import { Input } from '../shadcn/input';
import type { SelectOption } from '../select';

/**
 * From this many options up the panel opens with a search box. Below it the
 * whole list fits in the panel at once, and a box to narrow it is one more
 * thing to read past.
 */
const SEARCH_FROM = 8;

/**
 * A dropdown that keeps several values — the multi-pick sibling of `Select`.
 *
 * For a long list in a tight spot. When the options fit on the page, a list of
 * checkboxes is the better control: every choice stays visible, and nothing has
 * to be opened first.
 *
 * Takes what `Field` hands a control, so it binds the same way:
 * `<Field name="topics" label="…"><MultiSelect options={…} /></Field>`. The
 * value is a `string[]`; anything else (Field's `''` for an unset value) reads
 * as nothing picked.
 *
 * Unlike `Select`, focus moves into the panel while it is open: the search box
 * when there is one, otherwise the list itself. Picking does not close it —
 * the point is to pick several — so it closes on Done, Escape, Tab or a click
 * outside, and hands focus back to the trigger on the first two.
 */
export function MultiSelect({
  id,
  value,
  options,
  onChange,
  onBlur,
  placeholder = 'Choose any…',
  searchPlaceholder = 'Search',
  disabled,
  className,
  ref,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}: {
  id?: string;
  value?: unknown;
  options: readonly SelectOption[];
  onChange?: (next: string[]) => void;
  /** Fires once the panel has closed, so a form marks the field touched after the picking, not before. */
  onBlur?: () => void;
  /** Shown when nothing is picked. */
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
  /** Lets a form focus this control when it is the first one wrong on submit. */
  ref?: React.Ref<HTMLButtonElement>;
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const {
    anchor: trigger,
    panel,
    style,
    gapClass,
  } = useAnchoredPanel<HTMLButtonElement, HTMLDivElement>(open);

  const picked = Array.isArray(value) ? (value as string[]) : [];
  const searchable = options.length >= SEARCH_FROM;
  const shown = query
    ? options.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase()))
    : options;

  // In option order rather than pick order, so the summary does not reshuffle
  // as someone unticks and reticks.
  const pickedLabels = options.filter((o) => picked.includes(o.value)).map((o) => o.label);
  const summary =
    pickedLabels.length === 0
      ? placeholder
      : pickedLabels.length === 1
        ? pickedLabels[0]
        : `${pickedLabels[0]} +${pickedLabels.length - 1} more`;

  const close = (refocus: boolean) => {
    setOpen(false);
    setQuery('');
    if (refocus) trigger.current?.focus();
    onBlur?.();
  };

  // Pointer down, as in `Select`: a click on another control closes this
  // before that control reacts. The panel is portalled, so it is tested apart.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (trigger.current?.contains(target) || panel.current?.contains(target)) return;
      close(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
    // `close` is rebuilt every render; the listener only needs the latest `open`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Into the panel on open, a frame late: the anchored panel's first pass is
  // `visibility: hidden` while it measures, and a hidden element takes no focus.
  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() =>
      (searchRef.current ?? listRef.current)?.focus({ preventScroll: true }),
    );
    return () => cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    listRef.current?.children[active]?.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  const toggle = (optionValue: string) => {
    onChange?.(
      picked.includes(optionValue)
        ? picked.filter((v) => v !== optionValue)
        : // Rebuilt from `options` so the stored order matches the list.
          options.map((o) => o.value).filter((v) => v === optionValue || picked.includes(v)),
    );
  };

  /** Keys for whichever element holds focus in the panel — the search box or the list. */
  const onPanelKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      // Or the dialog this sits in takes the same Escape and closes too.
      e.stopPropagation();
      return close(true);
    }
    if (e.key === 'Tab') return close(false);
    const last = shown.length - 1;
    if (e.key === 'Enter' || (e.key === ' ' && e.target === listRef.current)) {
      e.preventDefault();
      const option = shown[active];
      if (option) toggle(option.value);
      return;
    }
    const to =
      e.key === 'ArrowDown'
        ? Math.min(active + 1, last)
        : e.key === 'ArrowUp'
          ? Math.max(active - 1, 0)
          : e.key === 'Home' && e.target === listRef.current
            ? 0
            : e.key === 'End' && e.target === listRef.current
              ? last
              : null;
    if (to === null) return;
    e.preventDefault();
    setActive(to);
  };

  return (
    <>
      <button
        ref={(node) => {
          trigger.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        id={id}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        disabled={disabled}
        onClick={() => {
          if (open) return close(false);
          setActive(0);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            e.preventDefault();
            setActive(0);
            setOpen(true);
          }
        }}
        onBlur={() => {
          // While open, focus has gone into the panel on purpose; the blur that
          // counts is the one `close` reports.
          if (!open) onBlur?.();
        }}
        className={cn(
          controlBase,
          controlStates,
          'h-9 items-center justify-between gap-2 text-left',
          open && 'border-ring ring-3 ring-brand/50',
          pickedLabels.length === 0 && 'text-ink-muted',
          className,
        )}
      >
        <span className="truncate">{summary}</span>
        <ChevronDownIcon
          aria-hidden="true"
          className={cn('size-4 shrink-0 text-ink-muted transition-transform', open && 'rotate-180')}
        />
      </button>

      {open &&
        createPortal(
          <div
            ref={panel}
            style={style}
            className={cn(
              'z-(--z-popover) flex max-h-(--menu-max-height) w-(--menu-max-width) max-w-screen flex-col rounded-xl border border-menu-edge bg-menu-bg p-1 text-menu-fg shadow-lift',
              gapClass,
            )}
          >
            {searchable && (
              <Input
                ref={searchRef}
                type="search"
                value={query}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                aria-controls={listId}
                aria-activedescendant={shown.length ? `${listId}-${active}` : undefined}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActive(0);
                }}
                onKeyDown={onPanelKeyDown}
                className="mb-1 shrink-0"
              />
            )}

            {shown.length === 0 ? (
              <p className="m-0 px-3 py-2.5 text-sm text-ink-muted">
                Nothing matches “{query.trim()}”.
              </p>
            ) : (
              <ul
                ref={listRef}
                id={listId}
                role="listbox"
                aria-multiselectable="true"
                aria-label={ariaLabel}
                aria-activedescendant={searchable ? undefined : `${listId}-${active}`}
                tabIndex={searchable ? undefined : -1}
                onKeyDown={searchable ? undefined : onPanelKeyDown}
                className="m-0 min-h-0 flex-1 list-none overflow-y-auto p-0 outline-none"
              >
                {shown.map((option, i) => {
                  const isPicked = picked.includes(option.value);
                  return (
                    <li
                      key={option.value}
                      id={`${listId}-${i}`}
                      role="option"
                      aria-selected={isPicked}
                      onPointerEnter={() => setActive(i)}
                      // Keep focus in the search box or the list rather than
                      // letting the row take it.
                      onPointerDown={(e) => e.preventDefault()}
                      onClick={() => toggle(option.value)}
                      className={cn(
                        'flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm md:text-base transition-colors',
                        i === active && 'bg-menu-option-active-bg text-menu-option-active-fg',
                        isPicked && 'text-menu-option-selected-fg',
                      )}
                    >
                      {/* The tick carries the state on its own, as in `Select` —
                          bolding the label would re-measure the row. */}
                      <CheckIcon
                        aria-hidden="true"
                        className={cn('size-4 shrink-0', !isPicked && 'invisible')}
                      />
                      <span className="truncate">{option.label}</span>
                    </li>
                  );
                })}
              </ul>
            )}

            <div className="mt-1 flex shrink-0 items-center justify-between gap-2 border-t border-hairline px-1 pt-2 pb-1">
              <span className="font-mono text-2xs text-ink-muted">{picked.length} selected</span>
              <span className="flex gap-1.5">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={picked.length === 0}
                  onClick={() => onChange?.([])}
                >
                  Clear
                </Button>
                <Button type="button" size="sm" onClick={() => close(true)}>
                  Done
                </Button>
              </span>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
